import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { 
  ExternalLink, 
  CheckCircle, 
  AlertCircle, 
  RefreshCw, 
  Shield,
  Info,
  LogIn,
  LogOut,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  getKiteLoginUrl,
  extractRequestToken,
  exchangeToken,
  isAuthenticated,
  getAuthState,
  clearAuthData,
  getLastAuthError,
  clearLastAuthError,
  fetchUserProfile
} from '@/utils/kiteConnect';

interface KiteConnectAuthProps {
  onAuthSuccess?: (userData: any) => void;
  onAuthError?: (error: string) => void;
  onAuthClear?: () => void;
}

export default function KiteConnectAuth({ 
  onAuthSuccess, 
  onAuthError,
  onAuthClear 
}: KiteConnectAuthProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [authState, setAuthState] = useState(getAuthState());
  const [userProfile, setUserProfile] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [supabaseSessionValid, setSupabaseSessionValid] = useState<boolean>(true);

  // Check Supabase session status
  const checkSupabaseSession = async () => {
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: { session }, error } = await supabase.auth.getSession();
      
      const isValid = !error && !!session;
      setSupabaseSessionValid(isValid);
      
      if (!isValid) {
        console.warn('Supabase session is invalid or missing');
      }
      
      return isValid;
    } catch (err) {
      console.error('Error checking Supabase session:', err);
      setSupabaseSessionValid(false);
      return false;
    }
  };

  // Initialize component
  useEffect(() => {
    // Check Supabase session first
    checkSupabaseSession();
    
    // Check for existing auth state
    const currentState = getAuthState();
    setAuthState(currentState);

    // Check for auth error
    const authError = getLastAuthError();
    if (authError) {
      setError(authError);
      clearLastAuthError();
    }

    // Check for request token in URL (callback handling) - ULTRA FAST MODE
    const requestToken = extractRequestToken();
    if (requestToken) {
      console.log('🚀 ULTRA-FAST MODE: Request token found, starting IMMEDIATE exchange...');
      // Start token exchange immediately with zero delay
      setTimeout(() => handleTokenExchange(requestToken), 0); // Use setTimeout to avoid blocking the UI
    }

    // Fetch user profile if authenticated
    if (currentState.isAuthenticated && !userProfile) {
      fetchProfile();
    }
  }, []);

  // Handle token exchange - OPTIMIZED FOR SPEED
  const handleTokenExchange = async (requestToken: string) => {
    const exchangeStartTime = Date.now();
    setIsLoading(true);
    setError(null);

    console.log('🚀 ULTRA-FAST TOKEN EXCHANGE STARTED');
    console.log('Component exchange start time:', new Date().toISOString());
    console.log('Request token received:', requestToken?.substring(0, 8) + '...');

    try {
      // Skip unnecessary delays and call exchange immediately
      const result = await exchangeToken(requestToken);
      
      const exchangeEndTime = Date.now();
      const componentExchangeTime = exchangeEndTime - exchangeStartTime;
      
      console.log('🚀 COMPONENT EXCHANGE COMPLETED');
      console.log('Component exchange time:', componentExchangeTime, 'ms');
      console.log('Token exchange result:', result);
      
      if (result.success) {
        // Update state immediately
        const newAuthState = getAuthState();
        setAuthState(newAuthState);
        
        console.log('Auth state updated, fetching profile...');
        
        // Clean up URL immediately (don't wait for profile fetch)
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Fetch user profile (non-blocking for success feedback)
        fetchProfile().catch(err => console.warn('Profile fetch error (non-critical):', err));
        
        toast.success('Successfully connected to Zerodha!');
        onAuthSuccess?.(result);
      } else {
        const errorMessage = result.error || 'Token exchange failed';
        console.error('Token exchange failed:', errorMessage);
        throw new Error(errorMessage);
      }
    } catch (err: any) {
      const errorTime = Date.now();
      const componentErrorTime = errorTime - exchangeStartTime;
      
      console.error('🚀 COMPONENT EXCHANGE FAILED');
      console.error('Component time before error:', componentErrorTime, 'ms');
      console.error('Token exchange error:', err);
      
      let errorMessage = 'Authentication failed';
      
      // Extract more specific error information
      if (err.message) {
        errorMessage = err.message;
      } else if (err.error) {
        errorMessage = err.error;
      }
      
      // Check for specific Kite API errors
      if (errorMessage.includes('api_key') && errorMessage.includes('minimum 6 characters')) {
        errorMessage = 'API key configuration error. Please check your Kite Connect app settings.';
      } else if (errorMessage.includes('Token exchange failed: 400')) {
        errorMessage = 'Invalid request token. Please try connecting again.';
      } else if (errorMessage.includes('Token exchange failed: 403')) {
        errorMessage = 'Authentication failed. Please check your Kite Connect app permissions.';
      } else if (errorMessage.includes('Request token has expired')) {
        errorMessage = 'Request token has expired. Please try connecting again - tokens expire within 2-3 minutes.';
      } else if (errorMessage.includes('TokenException')) {
        errorMessage = 'Authentication token expired. Please try connecting again.';
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
      onAuthError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch user profile
  const fetchProfile = async () => {
    try {
      const profileResult = await fetchUserProfile();
      
      if (profileResult.success) {
        setUserProfile(profileResult.profile);
      } else {
        console.warn('Failed to fetch user profile:', profileResult.error);
      }
    } catch (err) {
      console.warn('Error fetching user profile:', err);
    }
  };

  // Clear only Kite Connect authentication data (preserve Supabase session)
  const clearKiteAuthData = () => {
    console.log('Clearing Kite Connect authentication data for fresh OAuth flow...');
    
    // Clear Kite Connect data using the utility function
    clearAuthData();
    
    // Clear any cached auth errors
    clearLastAuthError();
    
    // Clear component state
    setAuthState(getAuthState());
    setUserProfile(null);
    setError(null);
    
    // Clear only specific Kite-related localStorage items (preserve Supabase auth)
    const kiteSpecificKeys = [
      'kite_access_token',
      'kite_user_data', 
      'kite_auth_error',
      'kite_use_simulation',
      'kite_request_token',
      'kite_login_time'
    ];
    
    const removedKeys = [];
    kiteSpecificKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        removedKeys.push(key);
      }
    });
    
    console.log('Cleared specific Kite Connect data:', removedKeys);
    console.log('Supabase authentication preserved');
  };

  // Handle login with fresh session and Supabase validation
  const handleLogin = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('=== STARTING FRESH OAUTH FLOW ===');
      console.log('Login start time:', new Date().toISOString());
      
      // Validate Supabase session before proceeding
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('Supabase session validation failed:', sessionError);
        setError('You must be logged into the application before connecting Zerodha. Please login first.');
        toast.error('Please login to the application first');
        setIsLoading(false);
        return;
      }
      
      console.log('Supabase session validated successfully');
      console.log('User ID:', session.user.id);
      
      // Clear only Kite Connect authentication data (preserve Supabase session)
      clearKiteAuthData();
      
      const loginUrl = getKiteLoginUrl();
      console.log('Redirecting to Zerodha OAuth...');
      
      // Open login in same window
      window.location.href = loginUrl;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to generate login URL';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    clearAuthData();
    setAuthState(getAuthState());
    setUserProfile(null);
    setError(null);
    
    toast.success('Disconnected from Zerodha');
    onAuthClear?.();
  };

  // Handle retry
  const handleRetry = () => {
    setError(null);
    if (authState.isAuthenticated) {
      fetchProfile();
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Zerodha Kite Connect
              </CardTitle>
              <CardDescription>
                Connect your Zerodha account to access portfolio data and AI insights
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {authState.isAuthenticated ? (
                <Badge variant="default" className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Connected
                </Badge>
              ) : (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Not Connected
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {!supabaseSessionValid && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Main application session expired. Please refresh the page and login again.
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <span>{error}</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRetry}
                  className="ml-2"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {!authState.isAuthenticated ? (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="space-y-2">
                    <h4 className="font-medium text-blue-900">
                      Connect your Zerodha account
                    </h4>
                    <p className="text-sm text-blue-800">
                      You'll be redirected to Zerodha's secure login page. After logging in, 
                      you'll be redirected back to complete the connection.
                    </p>
                    <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                      <p className="text-xs text-amber-800">
                        <strong>Important:</strong> Complete the login process quickly - tokens expire within 2-3 minutes.
                      </p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded p-2 mt-2">
                      <p className="text-xs text-green-800">
                        <strong>Session Management:</strong> Only Kite Connect data is cleared - your main app login remains intact.
                      </p>
                    </div>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Access your portfolio holdings and positions</li>
                      <li>• Get AI-powered investment insights</li>
                      <li>• Track your portfolio performance</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleLogin}
                disabled={isLoading || !supabaseSessionValid}
                className="w-full bg-gradient-to-r from-[#9E7C47] to-[#F9F1A9] hover:from-[#8A6B3A] hover:to-[#9E7C47] text-black font-medium"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <LogIn className="h-4 w-4 mr-2" />
                    Connect with Zerodha
                    <ExternalLink className="h-3 w-3 ml-2" />
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* User Profile Information */}
              {userProfile && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 p-2 rounded-full">
                      <User className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-green-900">
                        {userProfile.user_name || authState.userData?.userName || 'Connected User'}
                      </h4>
                      <p className="text-sm text-green-700">
                        User ID: {userProfile.user_id || authState.userData?.userId}
                      </p>
                      {userProfile.email && (
                        <p className="text-sm text-green-700">
                          Email: {userProfile.email}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Connection Status */}
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <h4 className="font-medium text-green-900">
                      Successfully Connected
                    </h4>
                    <p className="text-sm text-green-700">
                      Your Zerodha account is connected and ready to use.
                    </p>
                  </div>
                </div>
              </div>

              {/* Available Features */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Available Features</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Access your portfolio holdings and positions</li>
                  <li>• Get AI-powered investment insights</li>
                  <li>• Real-time portfolio analysis</li>
                  <li>• Track performance metrics</li>
                </ul>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button 
                  onClick={fetchProfile}
                  variant="outline"
                  disabled={isLoading}
                  className="flex-1"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Profile
                </Button>
                
                <Button 
                  onClick={handleLogout}
                  variant="destructive"
                  disabled={isLoading}
                  className="flex-1"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Disconnect
                </Button>
              </div>
            </div>
          )}

          {/* Development Mode Notice */}
          {import.meta.env.DEV && (
            <div className="space-y-2">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Development mode: Enable simulation by setting localStorage 'kite_use_simulation' to 'true'
                </AlertDescription>
              </Alert>
              
              <div className="flex gap-2">
                <Button 
                  onClick={clearKiteAuthData}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  🧹 Clear Kite Auth Data
                </Button>
                <Button 
                  onClick={() => {
                    localStorage.setItem('kite_use_simulation', 'true');
                    toast.success('Simulation mode enabled');
                  }}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  🧪 Enable Simulation
                </Button>
                <Button 
                  onClick={() => {
                    localStorage.removeItem('kite_use_simulation');
                    toast.success('Simulation mode disabled');
                  }}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  🚀 Use Real API
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}